import type { TextFieldProps as MuiTextFieldProps, SxProps } from '@mui/material';
import { Box, InputAdornment, TextField as MuiTextField, styled, Typography } from '@mui/material';
import React from 'react';
import type { FieldError } from 'react-hook-form';
import { EColors, getColor } from 'theme';

type SizeType = 'small' | 'medium';

type TextFieldProps = {
    label?: string;
    placeholder?: string;
    error?: FieldError;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    onKeyUp?: React.KeyboardEventHandler<HTMLInputElement>;
    value?: string | number;
    disabled?: boolean;
    required?: boolean;
    name?: string;
    type?: React.HTMLInputTypeAttribute;
    width?: string;
    suffix?: string;
    onSuffixClick?: () => void;
    prefix?: string;
    size?: SizeType;
    hideColors?: boolean;
    autoFocus?: boolean;
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
    sx?: SxProps;
};

type StyledTextFieldProps = MuiTextFieldProps & {
    hasError: boolean;
    customWidth?: string;
    size: SizeType;
    hideColors?: boolean;
};

const StyledTextField = styled(MuiTextField, {
    shouldForwardProp: prop => !['hasError', 'customWidth', 'size', 'hideColors'].includes(prop as string),
})<StyledTextFieldProps>(({ hasError, customWidth, size, hideColors }) => {
    const fontSize = size === 'small' ? '.75rem' : '.875rem';
    const padding = size === 'small' ? '14px' : '17px';
    const height = size === 'small' ? '.75rem' : '.875rem';

    return {
        flex: 1,
        boxSizing: 'border-box',
        width: customWidth || '100%',
        fontSize,

        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: getColor(EColors.v3Background),
        },

        '& .MuiOutlinedInput-notchedOutline': {
            border: hasError
                ? `1px solid ${getColor(EColors.red)}`
                : hideColors
                  ? 'none'
                  : `1px solid ${getColor(EColors.v3Border)}`,
            borderRadius: '12px',
        },

        // Keep red border on focus/hover when there's an error (override MUI theme)
        ...(hasError && {
            '& .MuiOutlinedInput-root.Mui-focused > .MuiOutlinedInput-notchedOutline': {
                borderColor: `${getColor(EColors.red)} !important`,
                borderWidth: '1px !important',
            },
            '& .MuiOutlinedInput-root:hover > .MuiOutlinedInput-notchedOutline': {
                borderColor: `${getColor(EColors.red)} !important`,
            },
        }),

        '& .MuiOutlinedInput-input': {
            borderRadius: '12px',
            padding,
            paddingLeft: '16px',
            fontSize,
            height,
        },

        // Hide increment/decrement arrows for number inputs
        '& .MuiOutlinedInput-input[type="number"]::-webkit-outer-spin-button, & .MuiOutlinedInput-input[type="number"]::-webkit-inner-spin-button':
            {
                WebkitAppearance: 'none',
                margin: 0,
            },

        '& .MuiOutlinedInput-input[type="number"]': {
            MozAppearance: 'textfield',
            // Additional styling to ensure consistent appearance
            '&::-ms-clear, &::-ms-expand': {
                display: 'none',
            },
        },

        // Prevent scroll wheel from changing number input values
        '& .MuiOutlinedInput-input[type="number"]:focus': {
            outline: 'none',
        },

        '& .MuiFormLabel-root': {
            transform: size === 'small' ? 'translate(16px, 12px)' : 'translate(16px, 14px)',
            fontSize,
            color: getColor(EColors.pureBlack, 0.5),
            '&.Mui-focused': {
                color: getColor(EColors.pureBlack, 0.6),
            },
        },

        '& .MuiFormLabel-root.MuiInputLabel-shrink, &:has(input:-webkit-autofill) .MuiFormLabel-root': {
            color: getColor(EColors.pureBlack, 0.6),
            fontSize,
            transform: 'translate(9px, -50%) scale(1)',
            backgroundColor: getColor(EColors.pureWhite),
            padding: '0 5px',
            lineHeight: '1em',
        },

        '&.MuiTextField-root:hover .MuiInputBase-root:not(.Mui-disabled) > fieldset.MuiOutlinedInput-notchedOutline': {
            borderColor: hasError ? `${getColor(EColors.red)}` : `${getColor(EColors.v3Dark)}`,
        },

        '& .MuiInputAdornment-root > p': {
            color: getColor(EColors.v3Dark, 0.3),
            fontSize: size === 'small' ? '.875rem' : '1rem',
        },

        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitTextFillColor: getColor(EColors.v3TextPrimary),
            WebkitBoxShadow: `0 0 0px 1000px ${getColor(EColors.v3Background)} inset`,
            transition: 'background-color 5000s ease-in-out 0s',
        },

        '& input:-webkit-autofill::placeholder, & input:-webkit-autofill:hover::placeholder, & input:-webkit-autofill:focus::placeholder':
            {
                color: 'transparent',
                opacity: 0,
            },
    };
});

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
    (
        {
            label,
            error,
            type = 'text',
            placeholder,
            required = false,
            width = '',
            suffix,
            onSuffixClick,
            prefix,
            size = 'medium',
            hideColors = false,
            inputProps,
            sx,
            ...field
        },
        ref
    ) => {
        const inputRef = React.useRef<HTMLInputElement>(null);

        // Combine the forwarded ref with our local ref
        React.useImperativeHandle(ref, () => inputRef.current!);

        // Prevent scroll wheel from changing number input values
        React.useEffect(() => {
            const input = inputRef.current;
            if (!input || type !== 'number') return;

            const preventScroll = (e: WheelEvent) => {
                e.preventDefault();
            };

            // Use addEventListener with passive: false to allow preventDefault
            input.addEventListener('wheel', preventScroll, { passive: false });

            return () => {
                input.removeEventListener('wheel', preventScroll);
            };
        }, [type]);

        return (
            <Box>
                <StyledTextField
                    inputRef={inputRef}
                    type={type}
                    size={size}
                    sx={sx}
                    hideColors={hideColors}
                    label={label}
                    hasError={!!error}
                    customWidth={width}
                    value={field.value ?? ''}
                    required={required}
                    disabled={field.disabled}
                    placeholder={placeholder}
                    autoFocus={field.autoFocus}
                    onBlur={field.onBlur}
                    onKeyUp={field.onKeyUp}
                    onChange={field.onChange}
                    inputProps={inputProps}
                    InputLabelProps={{
                        shrink: (field.value !== '' && field.value != null) || undefined,
                    }}
                    InputProps={{
                        startAdornment: prefix ? <InputAdornment position="start">{prefix}</InputAdornment> : undefined,
                        endAdornment: suffix ? (
                            <InputAdornment position="end">
                                {onSuffixClick ? (
                                    <Typography
                                        component="span"
                                        onClick={onSuffixClick}
                                        sx={{
                                            cursor: 'pointer',
                                            color: getColor(EColors.v3TextPrimary),
                                            fontSize: 'inherit',
                                            borderLeft: `1px solid ${getColor(EColors.v3Border)}`,
                                            paddingLeft: '10px',
                                            userSelect: 'none',
                                        }}
                                    >
                                        {suffix}
                                    </Typography>
                                ) : (
                                    suffix
                                )}
                            </InputAdornment>
                        ) : undefined,
                    }}
                />
                {error && (
                    <Typography variant="body2" color={getColor(EColors.red)} fontWeight={500}>
                        {error.message ?? 'Something went wrong'}
                    </Typography>
                )}
            </Box>
        );
    }
);
